import { useEffect, useState, useRef } from 'react';
import api from '../lib/api';

/**
 * Live preview of the technical drawing for a line item.
 * Debounces on input changes and fetches SVG from /api/drawing/preview.
 */
export default function DrawingPreview({ item, className = '', onDesignTypeResolved }) {
  const [svg, setSvg] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    const w = parseFloat(item?.width_mm) || 0;
    const h = parseFloat(item?.height_mm) || 0;
    const design = (item?.design_type || '').trim();

    if (!design || w <= 0 || h <= 0) {
      setSvg('');
      setError('Enter design type, width & height to preview drawing.');
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError('');
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const body = {
          design_type: design,
          width_mm: w,
          height_mm: h,
          has_mesh: !!(item.net_type && item.net_type !== 'None'),
          mesh_position: item.net_type?.toLowerCase().includes('fixed') ? 'fixed_panel'
            : item.net_type?.toLowerCase().includes('open') ? 'openable_panel' : null,
          hinge_side: item.hinge_side || null,
          shutters: item.shutters ? parseInt(item.shutters, 10) : null,
          horizontal_mullion_at_mm: item.horizontal_mullion_at_mm ? parseFloat(item.horizontal_mullion_at_mm) : null,
          profile_series: item.series || null,
          glass_type: item.glass_type || null,
        };
        const { data } = await api.post('/drawing/preview', body, {
          responseType: 'text',
          signal: ctrl.signal,
        });
        setSvg(data);
        if (onDesignTypeResolved && data) onDesignTypeResolved(data);
      } catch (e) {
        if (e.name !== 'CanceledError' && e.name !== 'AbortError') {
          setError('Preview failed. Check console.');
        }
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [item?.design_type, item?.width_mm, item?.height_mm, item?.net_type, item?.hinge_side, item?.shutters, item?.horizontal_mullion_at_mm, item?.series, item?.glass_type, onDesignTypeResolved]);

  return (
    <div className={`bg-white border border-slate-200 rounded-lg ${className}`} data-testid="drawing-preview">
      <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between rounded-t-lg">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Technical Drawing Preview</span>
        {loading && <span className="text-[10px] text-slate-400">Loading…</span>}
      </div>
      <div className="p-2 flex items-center justify-center min-h-[260px]">
        {error && <p className="text-[11px] text-slate-400 text-center px-4">{error}</p>}
        {svg && (
          <div
            className="w-full max-w-[300px]"
            dangerouslySetInnerHTML={{ __html: svg }}
          />
        )}
      </div>
    </div>
  );
}
